import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { gridContainer, breakpoints } from 'styles';
import { LocalContext, FirebaseContext } from 'context';
import { Button, LoadingSpinner } from 'components';
import { motion, AnimatePresence } from 'framer-motion';
import { wrap } from '@popmotion/popcorn';
import { graphql, useStaticQuery } from 'gatsby';
import LeftArrow from 'assets/svgs/left-arrow.svg';
import RightArrow from 'assets/svgs/right-arrow.svg';
import DefaultCommenterAvatar from 'assets/svgs/commenter.svg';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
// import FB from 'assets/svgs/fb-icon.svg';
// import Email from 'assets/svgs/email-icon.svg';
// import IN from 'assets/svgs/linkedin-icon.svg';
// import TW from 'assets/svgs/twitter-icon.svg';
// import Commenter from 'assets/svgs/commenter.svg';

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    };
  }
};

const ProjectPreview = ({ startPoint }) => {
  const { mobile, theme } = useContext(LocalContext);
  const [readMoreOpen, setReadMoreOpen] = useState(false);
  const [[page, direction], setPage] = useState([startPoint, 0]);
  const { user, firebase } = useContext(FirebaseContext);
  const [userComment, setUserComment] = useState({
    text: '',
    sending: false
  });
  const [allComments, setAllComments] = useState([]);
  const [deleteCommentId, setDeleteCommentId] = useState();

  const data = useStaticQuery(
    graphql`
      query {
        allProject {
          edges {
            node {
              title
              author
              summary
              timestamp
              disableModal
              uid
              localPDF {
                publicURL
              }
              localPreviewImg {
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED
                    formats: [AUTO, WEBP]
                    width: 440
                    height: 623
                    placeholder: TRACED_SVG
                  )
                }
              }
            }
          }
        }
      }
    `
  );

  const projects = data.allProject.edges
    .filter(({ node }) => !node.disableModal)
    .map((project, index) => ({
      node: {
        ...project.node,
        index
      }
    }));

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  const projectIndex = wrap(0, projects.length, page);

  const project = projects[projectIndex].node;

  useEffect(() => {
    if (firebase) {
      const unsubscribe = firebase.subscribeToProjectComments({
        pid: `${project.uid}-${project.timestamp}`,
        onSnapshot: (snapshot) => {
          const snapshotComments = [];

          if (snapshot) {
            snapshot.docs.forEach((doc) => {
              snapshotComments.push({
                cid: doc.id,
                ...doc.data()
              });
            });
          }

          setAllComments(snapshotComments);
        }
      });
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [firebase, project.index]);

  const handleUserCommentChange = (e) => {
    e.persist();
    setUserComment({
      sending: false,
      text: e.target.value
    });
  };

  const handleUserCommentSubmit = (e) => {
    e.preventDefault();

    if (userComment.text !== '') {
      setUserComment((currentValues) => ({
        ...currentValues,
        sending: true
      }));
      firebase
        .postProjectComment({
          avatarUrl: user.avatarUrl,
          text: userComment.text,
          name: user.name,
          pid: `${project.uid}-${project.timestamp}`,
          institutionOrCompany: user.institutionOrCompany,
          timestamp: Date.now(),
          uid: user.uid
        })
        .then(() =>
          setUserComment({
            text: '',
            sending: false
          })
        );
    }
  };

  const handleDeleteComment = ({ cid }) => {
    firebase
      .deleteProjectComment({
        pid: `${project.uid}-${project.timestamp}`,
        cid
      })
      .catch(console.error);
  };

  return (
    <Backdrop>
      {mobile && (
        <Carousel theme={theme}>
          <PosterContainer theme={theme}>
            <LiveStream theme={theme}>
              <a href={project.localPDF.publicURL} target="__blank">
                <StreamPlaceholder
                  theme={theme}
                  image={getImage(project.localPreviewImg.childImageSharp)}
                />
                <span>View Poster</span>
              </a>
              <Prev theme={theme} onClick={() => paginate(-1)}>
                <LeftArrow />
              </Prev>
            </LiveStream>
            <ProjectDetails theme={theme}>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectAuthor theme={theme}>{project.author}</ProjectAuthor>
              {/* <ProjectPosition>{project.position}</ProjectPosition>
              <ProjectSocials>
                <ProjectSocial href={project.fbLink} theme={theme}>
                  <FB />
                </ProjectSocial>
                <ProjectSocial href={project.linkedinLink} theme={theme}>
                  <IN />
                </ProjectSocial>
                <ProjectSocial href={project.twLink} theme={theme}>
                  <TW />
                </ProjectSocial>
                <ProjectSocial href={project.emailLink} theme={theme}>
                  <Email />
                </ProjectSocial>
              </ProjectSocials>
              <ProjectTitles>
                {project.topics.map((topic, i) => (
                  <ProjectTitle key={i}>{topic}</ProjectTitle>
                ))}
              </ProjectTitles> */}
              <ProjectSummary theme={theme} open={readMoreOpen}>
                {project.summary}
              </ProjectSummary>
              <ReadMoreButton
                theme={theme}
                open={readMoreOpen}
                onClick={() => {
                  setReadMoreOpen(true);
                }}>
                Read More
              </ReadMoreButton>

              {firebase && (
                <ProjectComments theme={theme}>
                  {allComments.length > 0 ? (
                    <AnimatePresence>
                      {allComments.map((comment) => (
                        <motion.div
                          layout
                          key={comment.cid}
                          initial={{
                            scale: 0,
                            opacity: 0
                          }}
                          animate={{
                            scale: 1,
                            opacity: 1,
                            transition: {
                              duration: 0.6,
                              type: 'tween'
                            }
                          }}
                          exit={{
                            scale: 0,
                            opacity: 0,
                            transition: {
                              duration: 0.6,
                              type: 'tween'
                            }
                          }}>
                          {comment.avatarUrl ? (
                            <CommenterAvatar
                              size="1.625rem"
                              src={comment.avatarUrl}
                              alt={comment.name}
                              theme={theme}
                            />
                          ) : (
                            <DefaultCommenterAvatar />
                          )}
                          <CommentWrapper theme={theme}>
                            <p>
                              <span>{comment.name}</span>
                              {comment.text}
                            </p>
                            <small>{format(comment.timestamp, 'HH:mm')}</small>
                            {(user?.isAdmin || user?.uid === comment?.uid) && (
                              <DeleteComment
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleDeleteComment({ cid: comment.cid })}>
                                Delete
                              </DeleteComment>
                            )}
                          </CommentWrapper>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  ) : (
                    <p style={{ color: '#000' }}>Add a comment below...</p>
                  )}
                </ProjectComments>
              )}
              <Next theme={theme} onClick={() => paginate(1)}>
                <RightArrow />
              </Next>
            </ProjectDetails>
            <Comment>
              <CommentLabel htmlFor="comment" theme={theme}>
                Add Your Comment
              </CommentLabel>
              <AddComment>
                <UserAvatar>
                  {user?.avatarUrl ? (
                    <CommenterAvatar
                      size="2.5rem"
                      src={user.avatarUrl}
                      alt={user.name}
                      theme={theme}
                    />
                  ) : (
                    <DefaultCommenterAvatar />
                  )}
                </UserAvatar>
                <CommentInput
                  onChange={handleUserCommentChange}
                  value={userComment.text}
                  id="comment"
                  name="comment"
                  type="text"
                  placeholder="Insert your comment..."
                  theme={theme}
                />
              </AddComment>
            </Comment>
            {!mobile && (
              <Actions>
                <Cancel
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setUserComment({ text: '', sending: false })}>
                  Cancel
                </Cancel>
                <Button
                  type="button"
                  onClick={handleUserCommentSubmit}
                  disabled={userComment.text === ''}
                  width="8.5rem"
                  whileTap={userComment.text !== '' && { scale: 0.9 }}>
                  {userComment.sending ? <LoadingSpinner /> : 'Comment'}
                </Button>
              </Actions>
            )}
          </PosterContainer>
        </Carousel>
      )}
      {!mobile && (
        <Carousel
          theme={theme}
          key={page}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 50 }
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}>
          <AnimatePresence initial={false} custom={direction}>
            <PosterContainer theme={theme}>
              <LiveStream theme={theme}>
                <a href={project.localPDF.publicURL} target="__blank">
                  <StreamPlaceholder
                    theme={theme}
                    image={getImage(project.localPreviewImg.childImageSharp)}
                  />
                  <span>View Poster</span>
                </a>
                <Prev theme={theme} onClick={() => paginate(-1)}>
                  <LeftArrow />
                </Prev>
              </LiveStream>
              <ProjectDetails theme={theme}>
                <div>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectAuthor theme={theme}>{project.author}</ProjectAuthor>
                  {/* <ProjectPosition>{project.position}</ProjectPosition>
                  <ProjectSocials>
                    <ProjectSocial href={project.fbLink} theme={theme}>
                      <FB />
                    </ProjectSocial>
                    <ProjectSocial href={project.linkedinLink} theme={theme}>
                      <IN />
                    </ProjectSocial>
                    <ProjectSocial href={project.twLink} theme={theme}>
                      <TW />
                    </ProjectSocial>
                    <ProjectSocial href={project.emailLink} theme={theme}>
                      <Email />
                    </ProjectSocial>
                  </ProjectSocials>
                  <ProjectTitles>
                    {project.topics.map((topic, i) => (
                      <ProjectTitle key={i}>{topic}</ProjectTitle>
                    ))}
                  </ProjectTitles> */}
                  <ProjectSummary theme={theme} open={readMoreOpen}>
                    {project.summary}
                  </ProjectSummary>
                  <ReadMoreButton
                    theme={theme}
                    open={readMoreOpen}
                    onClick={() => {
                      setReadMoreOpen(true);
                    }}>
                    Read More
                  </ReadMoreButton>
                </div>
                {firebase && (
                  <ProjectComments theme={theme}>
                    {allComments.length > 0 ? (
                      <AnimatePresence>
                        {allComments.map((comment) => (
                          <motion.div
                            layout
                            key={comment.cid}
                            initial={{
                              scale: 0,
                              opacity: 0
                            }}
                            animate={{
                              scale: 1,
                              opacity: 1,
                              transition: {
                                duration: 0.6,
                                type: 'tween'
                              }
                            }}
                            exit={{
                              scale: 0,
                              opacity: 0,
                              transition: {
                                duration: 0.6,
                                type: 'tween'
                              }
                            }}>
                            {comment.avatarUrl ? (
                              <CommenterAvatar
                                size="1.625rem"
                                src={comment.avatarUrl}
                                alt={comment.name}
                                theme={theme}
                              />
                            ) : (
                              <DefaultCommenterAvatar />
                            )}
                            <CommentWrapper theme={theme}>
                              <p>
                                <span>{comment.name}</span>
                                {comment.text}
                              </p>
                              <small>{format(comment.timestamp, 'HH:mm')}</small>
                              {(user?.isAdmin || user?.uid === comment?.uid) && (
                                <DeleteComment
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleDeleteComment({ cid: comment.cid })}>
                                  Delete
                                </DeleteComment>
                              )}
                            </CommentWrapper>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    ) : (
                      <p style={{ color: '#000' }}>Add a comment below...</p>
                    )}
                  </ProjectComments>
                )}
                <Next theme={theme} onClick={() => paginate(1)}>
                  <RightArrow />
                </Next>
              </ProjectDetails>
              <Comment>
                <CommentLabel htmlFor="comment" theme={theme}>
                  Add Your Comment
                </CommentLabel>
                <AddComment>
                  <UserAvatar>
                    {user?.avatarUrl ? (
                      <CommenterAvatar
                        size="2.5rem"
                        src={user.avatarUrl}
                        alt={user.name}
                        theme={theme}
                      />
                    ) : (
                      <DefaultCommenterAvatar />
                    )}
                  </UserAvatar>
                  <CommentInput
                    onChange={handleUserCommentChange}
                    value={userComment.text}
                    id="comment"
                    name="comment"
                    type="text"
                    placeholder="Insert your comment..."
                    theme={theme}
                  />
                </AddComment>
              </Comment>
              {!mobile && (
                <Actions>
                  <Cancel
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setUserComment({ text: '', sending: false })}>
                    Cancel
                  </Cancel>
                  <Button
                    type="button"
                    onClick={handleUserCommentSubmit}
                    disabled={userComment.text === ''}
                    width="8.5rem"
                    whileTap={userComment.text !== '' && { scale: 0.9 }}>
                    {userComment.sending ? <LoadingSpinner /> : 'Comment'}
                  </Button>
                </Actions>
              )}
            </PosterContainer>
          </AnimatePresence>
        </Carousel>
      )}
    </Backdrop>
  );
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const Backdrop = styled.div`
  max-width: 100%;
  height: auto;
  margin-top: -20px;
  margin-left: -20px;
  width: calc(100vw + 40px);
`;

const Carousel = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  min-height: 100vh;
  width: calc(100vw - 20px);
  color: ${({ theme }) => theme.textColor};
  background: ${({ theme }) => (theme.className === 'contrast' ? theme.bgLight : 'white')};
`;

const PosterContainer = styled.div`
  position: relative;
  ${gridContainer}
  width:1200px;
  max-width: 100%;
  height: auto;
  padding: 5rem 1rem;
  padding-right: 1.5rem;
  align-items: center;
  h3,
  label,
  input {
    color: ${({ theme }) => theme.TextColor} !important;
  }
  @media (min-width: 768px) {
    padding: 5rem 3rem;
  }
  @media (min-width: 1150px) {
    padding: 2.2rem 7.5rem;
  }
`;

const LiveStream = styled.div`
  grid-column: 1/7;
  height: auto;
  margin: 1.25rem 0;
  position: relative;
  a {
    display: flex;
    flex-direction: column;
    span {
      margin: 2rem auto;
      text-decoration: underline;
    }
    color: ${({ theme }) => theme.textColor};
  }

  @media (min-width: 768px) {
    grid-column: 1/3;
  }
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 1/5;
    margin: 0px;
    height: 100%;
  }
`;

const StreamPlaceholder = styled(GatsbyImage)`
  width: 450px;
  max-width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
  border-radius: ${({ cropped }) => (cropped ? '50%' : '0px')};
  transition: 0.5s;
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.className === 'contrast' ? 'none' : '0px.375rem 0.75rem rgba(0,0,0,.25)'};
`;

const ProjectDetails = styled.div`
  grid-column: 1/7;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  color: ${({ theme }) => theme.textColor};
  @media (min-width: 768px) {
    grid-column: 4/7;
  }
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 6/11;
  }
`;

const ProjectTitle = styled.h2`
  margin-bottom: 1rem;
  margin-right: 0.375rem;
  font-size: 1.25rem;
  line-height: 1.5em;
`;

const ProjectAuthor = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1.3em;
  margin-top: 0px;
  margin-bottom: 1.5rem;
  text-align: left;
`;

const ProjectSummary = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: ${({ open }) => (open ? 'none' : '5')};
  -webkit-box-orient: vertical;
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: ${({ open }) => (open ? '3rem' : '.5rem')};
  max-width: 100%;
  padding-right: 10px;
  height: ${({ open }) => (open ? '12rem' : 'auto')};
  overflow-y: ${({ open }) => (open ? 'scroll' : 'hidden')};
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  outline: none;
  height: 3rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Helvetica Neue';
  text-transform: uppercase;
  color: ${({ theme }) => theme.fg};
  background-color: transparent;
  cursor: pointer;
  transition: 0.5s;
  width: auto;
  display: ${({ open }) => (open ? 'none' : 'flex')};
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProjectComments = styled.div`
  width: 100%;
  height: 12rem;
  margin-bottom: 0px;
  padding: 1.25rem;
  border: 1px solid;
  background: ${({ theme }) => (theme.className === 'contrast' ? theme.bgLight : 'white')};
  overflow-y: scroll;
  position: relative;

  > div {
    display: flex;

    img,
    svg {
      flex-shrink: 0;
      margin-right: 0.625rem;
      width: 1.625rem;
    }

    > div {
      bottom: 0.375rem;
      position: relative;

      p {
        font-size: 1rem;
        line-height: 1.25em;
        margin-top: 0.575em;
      }

      small {
        bottom: 0.125em;
        font-size: 0.65rem;
        left: 0.063em;
        position: relative;
      }

      span {
        color: #c4c4c4;
        font-weight: 700;
        margin-right: 0.475rem;
      }
    }
  }
`;

const CommentWrapper = styled.div`
  margin-bottom: 0.75em;

  p,
  small {
    color: ${({ theme }) => theme.textColor};
  }
`;

const DeleteComment = styled(motion.small)`
  cursor: pointer;
  margin-left: 0.5em;
  text-decoration: underline;
`;

const Comment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 2.5rem 0px;
  grid-column: 1/7;
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 1/11;
  }
`;

const CommentLabel = styled.label`
  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 2rem;
  width: 100%;
`;

const AddComment = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const CommentInput = styled.input`
  border: none;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: 0.375rem;
  background: transparent;

  font-weight: 400;
  letter-spacing: 0.5px;
  font-size: 1rem;
  line-height: 2rem;
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.textColor};
  ::placeholder {
    font-style: italic;
    font-weight: 400;
    font-size: 1rem;
    color: #bdbdbd;
  }
`;

const UserAvatar = styled.div`
  grid-column: 1/2;
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  svg {
    width: 2.5rem;
    height: 2.5rem;
  }
`;

const CommenterAvatar = styled.img`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
  object-fit: cover;
  border: 0.125rem solid ${({ theme }) => theme.fg};
`;

const Actions = styled.div`
  grid-column: 8/12;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2.5rem;
`;

const Cancel = styled(motion.button)`
  align-items: center;
  background: none;
  border: none;
  color: #5a5a5b;
  cursor: pointer;
  display: flex;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  height: 3rem;
  justify-content: center;
  line-height: 1.25rem;
  margin-right: 0.625rem;
  outline: none;
  text-transform: uppercase;
  width: 7.5rem;
`;

const Prev = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
  justify-self: end;
  background-color: ${({ theme }) => theme.fg};
  padding-left: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  left: -4.5rem;
  top: 50%;
  transform: translateY(-50%);
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: translateY(-50%) scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

const Next = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
  justify-self: start;
  background-color: ${({ theme }) => theme.fg};
  padding-right: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -4.5rem;
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: translateY(-50%) scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

// const ProjectTitles = styled.p`
//   text-align: left;
//   font-size: 1.25rem;
//   margin-bottom: 2rem;
//   margin-top: 0.5rem;
// `;

// const ProjectPosition = styled.p`
//   margin-top: 0px;

//   font-style: normal;
//   font-weight: 400;
//   font-size: 1rem;
//   line-height: 2rem;
//   margin-bottom: 0.375rem;
//   text-align: center;
// `;

export default ProjectPreview;
